import { Component, ErrorInfo, ReactNode } from "react";
import { SnowField } from "@/components/SnowField";
import i18nInstance from "@/i18n";
import { reportClientError } from "@/lib/errorReporter";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  // Captured at componentDidCatch — surfaced in a collapsible "What broke"
  // panel inside the fallback so a family (or a teammate triaging a bug
  // report) can read the actual error name, message, and the first few
  // lines of the React component stack without DevTools open. We keep it
  // compact and on-brand (small mono text, dimmed) so it never overshadows
  // the storybook fallback copy.
  errorName: string | null;
  errorMessage: string | null;
  componentStackHead: string | null;
};

// localStorage key used to persist the last captured error across the
// retry / "Back to JINGLE" reload, so a follow-up bug report still has the
// original stack even after the user navigates away. Capped to a single
// entry — we never want a growing log of past errors on the device.
const LAST_ERROR_KEY = "jingle:lastErrorBoundary:v1";

// Trim a React component stack down to the first few frames so it fits in
// the fallback panel without dwarfing the rest of the page.
function headOfStack(stack: string | null | undefined, lines = 6): string | null {
  if (!stack) return null;
  return stack
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, lines)
    .join("\n");
}

// English fallback copy. We never want a non-English family to suddenly see
// English on a crash — but we ALSO never want the fallback itself to crash
// because the i18n module failed to load. These strings are the last-ditch
// defaults if `i18nInstance.t` is missing or throws.
const FALLBACK_EN = {
  kicker: "A hiccup at the workshop",
  headline: "Something broke at the North Pole",
  body: "One of the elves dropped a snow globe. We've already swept up the shards — try again, or head back to the warmth of the fire.",
  tryAgain: "Try again",
  backHome: "Back to JINGLE",
} as const;

type FallbackKey = keyof typeof FALLBACK_EN;

// Resolve an `errorBoundary.*` key from i18next, falling back to the English
// copy above whenever i18n hasn't initialized, the key is missing, or the
// call itself throws. We deliberately read the singleton instance instead
// of calling `useTranslation()` so this works even if React-i18next's
// provider chain isn't set up where the boundary lives.
function localized(key: FallbackKey): string {
  const fallback = FALLBACK_EN[key];
  try {
    if (!i18nInstance || typeof i18nInstance.t !== "function") return fallback;
    const value = i18nInstance.t(`errorBoundary.${key}`, {
      defaultValue: fallback,
    });
    return typeof value === "string" && value.length > 0 ? value : fallback;
  } catch {
    return fallback;
  }
}

// Top-level boundary that catches any uncaught render error in the JINGLE
// router subtree. Without this, a thrown component would leave the user on a
// blank white screen — completely outside the storybook brand. The fallback
// mirrors the visual vocabulary of the 404 page (storybook bg, snow,
// gold flourish, serif headline, ornament-rimmed buttons) so even a crash
// still feels like part of the world. Copy is pulled from the i18n catalog
// (with English defaults) so a French- or Spanish-speaking family sees the
// fallback in their own language. The raw error is logged to the console
// for debugging but never surfaced to the user.
export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    errorName: null,
    errorMessage: null,
    componentStackHead: null,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      errorName:
        (error && (error.name || (error as { constructor?: { name?: string } }).constructor?.name)) ||
        "Error",
      errorMessage: (error && (error.message || String(error))) || "Unknown error",
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const stackHead = headOfStack(info.componentStack);
    // eslint-disable-next-line no-console
    console.error("[JINGLE] Uncaught render error:", error, info);
    this.setState({ componentStackHead: stackHead });
    // Persist last error to localStorage so a subsequent reload (or bug
    // report after the user clicks "Back to JINGLE") still surfaces the
    // original cause. Cap the payload, swallow any storage failures.
    try {
      const payload = JSON.stringify({
        at: new Date().toISOString(),
        url: typeof window !== "undefined" ? window.location.href : "",
        name: error?.name ?? "Error",
        message: error?.message ?? String(error),
        stack: (error?.stack ?? "").slice(0, 4000),
        componentStack: stackHead ?? "",
      });
      window.localStorage.setItem(LAST_ERROR_KEY, payload);
    } catch {
      /* private mode / quota — fine */
    }
    // Fire-and-forget: report to the api-server so a crash that hits a
    // real family is actually visible to us. The reporter handles its
    // own dedup, PII scrub, and never throws.
    reportClientError({
      source: "react_error_boundary",
      error,
      componentStack: info.componentStack ?? null,
    });
  }

  private handleRetry = (): void => {
    // Clear the error state and let React try to re-render the subtree. If the
    // underlying problem has resolved (e.g. a transient network blip) the app
    // recovers in place; if it hasn't, the boundary will catch again and we
    // stay on the fallback.
    this.setState({
      hasError: false,
      errorName: null,
      errorMessage: null,
      componentStackHead: null,
    });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const kicker = localized("kicker");
    const headline = localized("headline");
    const body = localized("body");
    const tryAgain = localized("tryAgain");
    const backHome = localized("backHome");

    return (
      <div
        className="dark jingle-storybook-bg relative min-h-screen overflow-hidden text-jingle-snow"
        data-testid="error-boundary-fallback"
      >
        <SnowField flakeCount={70} />

        {/* Soft warm halo behind the headline — same vocabulary as the 404. */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, hsla(40, 95%, 65%, 0.45), transparent 65%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[40vh]"
          style={{
            background:
              "radial-gradient(120% 60% at 50% 100%, hsla(38, 78%, 55%, 0.22) 0%, transparent 60%)",
            filter: "blur(28px)",
          }}
        />

        <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
          <div
            aria-hidden
            className="flex items-center gap-3 text-jingle-gold-soft/80"
          >
            <span className="jingle-flourish-line" />
            <span className="text-xs">✦</span>
            <span className="jingle-flourish-line jingle-flourish-line--right" />
          </div>

          <p className="mt-4 text-[11px] uppercase tracking-[0.4em] text-amber-100/70">
            {kicker}
          </p>

          <h1
            className="mt-3 font-serif text-[2.75rem] font-medium leading-[1.05] text-amber-50 sm:text-[3.5rem]"
            style={{
              textShadow:
                "0 4px 30px hsla(40, 90%, 60%, 0.4), 0 2px 10px hsla(0, 80%, 55%, 0.35)",
            }}
          >
            {headline}
          </h1>

          <div className="jingle-hairline mt-6 w-40" />

          <p className="mt-6 max-w-sm text-base leading-relaxed text-amber-100/85">
            {body}
          </p>

          {/* Diagnostic panel — collapsed by default. Lets a family copy
              the actual error name + message + first few component-stack
              frames into a bug report without DevTools. We deliberately
              do not log on the console alone; that gets lost on a fresh
              tab. The panel is on-brand (small, dim, mono) so it never
              competes with the storybook copy above. */}
          {this.state.errorMessage ? (
            <details
              className="mt-6 w-full max-w-md rounded-xl border border-amber-200/15 bg-black/30 px-4 py-3 text-left text-amber-100/75 backdrop-blur"
              data-testid="details-error-info"
            >
              <summary className="cursor-pointer select-none text-[10px] font-semibold uppercase tracking-[0.32em] text-amber-200/80 hover:text-amber-100 focus:outline-none">
                {localized("kicker") /* "A hiccup at the workshop" */} ·{" "}
                <span className="not-italic text-amber-200/60">show details</span>
              </summary>
              <div className="mt-3 space-y-2 font-mono text-[11px] leading-snug">
                <p className="break-words text-rose-200/85">
                  <span className="text-amber-200/60">{this.state.errorName}:</span>{" "}
                  {this.state.errorMessage}
                </p>
                {this.state.componentStackHead ? (
                  <pre className="whitespace-pre-wrap break-words text-amber-100/65">
                    {this.state.componentStackHead}
                  </pre>
                ) : null}
              </div>
            </details>
          ) : null}

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={this.handleRetry}
              data-testid="button-retry"
              className="group inline-flex h-12 items-center justify-center gap-3 rounded-full border border-amber-200/40 bg-[hsl(354,55%,12%)]/60 px-7 text-xs font-semibold uppercase tracking-[0.32em] text-amber-50 backdrop-blur transition hover:border-amber-200/70 hover:bg-amber-200/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200/70"
            >
              <span aria-hidden className="text-amber-200/80">
                ✦
              </span>
              {tryAgain}
              <span aria-hidden className="text-amber-200/80">
                ✦
              </span>
            </button>

            <a
              href={import.meta.env.BASE_URL}
              data-testid="link-home"
              className="group inline-flex h-12 items-center justify-center gap-3 rounded-full border border-amber-200/20 bg-transparent px-7 text-xs font-semibold uppercase tracking-[0.32em] text-amber-100/85 transition hover:border-amber-200/50 hover:text-amber-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200/70"
            >
              {backHome}
            </a>
          </div>
        </div>
      </div>
    );
  }
}
